import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import '../styles/user-selector.css';

const UserSelector = ({ selectedUsers = [], onSelectionChange, disabled = false }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Load available users when component mounts and user is available
  useEffect(() => {
    if (user?.id) {
      loadUsers();
    }
  }, [user?.id]);

  const loadUsers = async () => {
    if (!user?.id) {
      console.log('UserSelector: No authenticated user found');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const userId = user.id;

      console.log('UserSelector: Loading users with userId:', userId);
      const response = await fetch(`${API_BASE}/api/user?operation=getAllUsers&userId=${userId}`);

      console.log('UserSelector: Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('UserSelector: Raw API response:', data);

        // Map the response to match our expected format
        const users = (data.users || []).map(user => ({
          id: user.id,
          name: user.displayName,
          email: user.email,
          role: user.role
        }));

        console.log('UserSelector: Mapped users:', users);
        setAvailableUsers(users);
      } else {
        const errorText = await response.text();
        console.error('Failed to load users. Status:', response.status, 'Error:', errorText);
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setAvailableUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = availableUsers.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  const handleUserToggle = (userId) => {
    const newSelection = selectedUsers.includes(userId)
      ? selectedUsers.filter(id => id !== userId)
      : [...selectedUsers, userId];

    onSelectionChange(newSelection);
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
    // Reload users to get fresh data if needed
    if (availableUsers.length === 0 && !loading) {
      loadUsers();
    }
  };

  const handleRemoveUser = (userId) => {
    const newSelection = selectedUsers.filter(id => id !== userId);
    onSelectionChange(newSelection);
  };

  const getSelectedUserNames = () => {
    return selectedUsers.map(userId => {
      const user = availableUsers.find(u => u.id === userId);
      return user ? user.name || user.email : userId;
    });
  };

  return (
    <div className="user-selector">
      <div className="user-selector-label">
        {t('metadata.restrictedUsersLabel')}
      </div>

      {/* Selected users display */}
      {selectedUsers.length > 0 && (
        <div className="selected-users">
          {selectedUsers.map(userId => {
            const user = availableUsers.find(u => u.id === userId);
            const displayName = user ? (user.name || user.email) : userId;

            return (
              <div key={userId} className="selected-user-tag">
                <span>{displayName}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveUser(userId)}
                    className="remove-user-btn"
                    title={t('metadata.removeUser')}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/manage users button and dropdown */}
      {!disabled && (
        <div className="user-search-container">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="add-users-button"
            disabled={loading || !user?.id}
          >
            {!user?.id ? t('metadata.authRequired') :
             loading ? t('metadata.loadingUsers') :
             t('metadata.manageUsers')}
          </button>

          {showDropdown && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span>{t('metadata.selectUsers')}</span>
                <button
                  type="button"
                  onClick={() => setShowDropdown(false)}
                  className="close-dropdown-btn"
                >
                  ×
                </button>
              </div>

              {/* Search filter inside dropdown */}
              <div className="dropdown-search">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('metadata.filterUsers')}
                  className="filter-input"
                  autoFocus
                />
              </div>

              <div className="user-list">
                {filteredUsers.length === 0 ? (
                  <div className="no-users">
                    {loading ? t('metadata.loadingUsers') : t('metadata.noUsersFound')}
                  </div>
                ) : (
                  filteredUsers.map(user => {
                    const isSelected = selectedUsers.includes(user.id);
                    const displayName = user.name || user.email || `User ${user.id.substring(0, 8)}`;
                    const showEmail = user.email && user.email !== displayName;

                    return (
                      <div
                        key={user.id}
                        className={`user-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleUserToggle(user.id)}
                      >
                        <div className="user-info">
                          <div className="user-name">
                            {displayName}
                            {user.role && user.role !== 'member' && (
                              <span className="user-role"> ({user.role})</span>
                            )}
                          </div>
                          {showEmail && (
                            <div className="user-email">{user.email}</div>
                          )}
                        </div>
                        <div className="selection-indicator">
                          {isSelected ? '✓' : ''}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {selectedUsers.length === 0 && (
        <div className="no-restrictions-note">
          {t('metadata.noRestrictionsNote')}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="dropdown-backdrop"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default UserSelector;